package com.arena_web.ui.sections.sports

import androidx.compose.runtime.Composable
import com.arena_web.generated.GENERATED_SPORT_CLUBS
import com.arena_web.ui.components.StoreBadges
import com.arena_web.ui.components.navigation.NavAnchor
import com.arena_web.ui.layouts.PageLayout
import com.arena_web.ui.sections.info.InfoPageScaffold
import com.arena_web.ui.sections.info.InfoParagraph
import com.arena_web.ui.sections.info.InfoSection
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.h1
import com.arena_web.ui.utils.Constants
import com.arena_web.ui.utils.isMobile
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.H1

data class SportSeo(
    val slug: String,
    val name: String,
    val seoTitle: String,
    val description: String,
    val h1: String,
    val teaser: String,
    val intro: String,
    val createJoin: String,
    val findPlayers: String,
    val clubs: String,
)

val ALL_SPORTS: List<SportSeo> = listOf(
    SportSeo(
        slug = "football",
        name = "Football",
        seoTitle = "Find Football Players Near You | Arena Pro Play",
        description = "Find football players near you on Arena Pro Play. Join local pickup games, create your own match, and meet players at your level in your city.",
        h1 = "Find Football Players Near You",
        teaser = "Pickup, 5-a-side and 11-a-side games near you.",
        intro = "Looking for people to play football with? Arena Pro Play connects you with footballers nearby — for 5-a-side, 7-a-side or full 11-a-side games. Find a match to join tonight or start your own and fill the roster.",
        createJoin = "Create a football game in seconds — set the place, time and how many players you need — or browse games happening near you and join with one tap. No more chasing group chats to get a match together.",
        findPlayers = "Find players at your level and position. Build your profile, see who's playing nearby, follow other footballers, and put together a team you actually enjoy playing with.",
        clubs = "Discover football pitches and clubs around you, see where locals play, and book a place for your next game — from neighbourhood cages to full-size pitches.",
    ),
    SportSeo(
        slug = "tennis",
        name = "Tennis",
        seoTitle = "Find a Tennis Partner Near You | Arena Pro Play",
        description = "Find a tennis partner near you on Arena Pro Play. Book a court, arrange singles or doubles, and meet players at your level in your city.",
        h1 = "Find Tennis Players Near You",
        teaser = "Singles, doubles and hitting partners near you.",
        intro = "Need a hitting partner or a doubles four? Arena Pro Play helps you find tennis players nearby who match your level, so you always have someone to rally with.",
        createJoin = "Arrange a match in a couple of taps — pick a court, a time, singles or doubles — or join a game someone near you has already set up.",
        findPlayers = "Find partners at your level, from casual rallies to competitive sets. Follow players you enjoy facing and build a regular hitting circle.",
        clubs = "Discover tennis courts and clubs near you, check where people play, and book a court for your next match.",
    ),
    SportSeo(
        slug = "basketball",
        name = "Basketball",
        seoTitle = "Find Basketball Players Near You | Arena Pro Play",
        description = "Find basketball players near you on Arena Pro Play. Join local pickup runs, organise 3x3 or 5x5 games, and meet players at your level in your city.",
        h1 = "Find Basketball Players Near You",
        teaser = "3x3 and 5x5 pickup runs near you.",
        intro = "Want to run some pickup basketball? Arena Pro Play connects you with hoopers nearby for 3x3 and full 5x5 games — find a run to join or start your own.",
        createJoin = "Set up a game — court, time, how many you need — or jump into a run happening near you. Getting a full squad together takes seconds, not days.",
        findPlayers = "Find players by position and level, follow the ones you like playing with, and build a regular crew for your local court.",
        clubs = "Discover courts, gyms and hoops near you, see where people play, and book a spot for your next game.",
    ),
    SportSeo(
        slug = "padel",
        name = "Padel",
        seoTitle = "Find Padel Players Near You | Arena Pro Play",
        description = "Find padel players near you on Arena Pro Play. Complete your doubles four, book a court, and meet players at your level in your city.",
        h1 = "Find Padel Players Near You",
        teaser = "Complete your doubles four near you.",
        intro = "Padel is best as a four — Arena Pro Play helps you find the players to complete it. Match with padel players nearby at your level and get on court faster.",
        createJoin = "Book a court and invite players, or join a game that's one or two players short near you. No more group-chat scramble to find a fourth.",
        findPlayers = "Find partners and opponents at your level, follow the players you enjoy, and build a regular padel group.",
        clubs = "Discover padel clubs and courts near you, see where locals play, and book your next session.",
    ),
    SportSeo(
        slug = "volleyball",
        name = "Volleyball",
        seoTitle = "Find Volleyball Players Near You | Arena Pro Play",
        description = "Find volleyball players near you on Arena Pro Play. Join indoor or beach games, form a team, and meet players at your level in your city.",
        h1 = "Find Volleyball Players Near You",
        teaser = "Indoor and beach games near you.",
        intro = "Indoor or on the sand — Arena Pro Play helps you find volleyball players nearby to make up a team or join one that needs players.",
        createJoin = "Set up a game or join one near you — pick the place, time and how many players you need, and fill the court in seconds.",
        findPlayers = "Find players by position and level, follow the ones you like playing with, and build a regular volleyball crew.",
        clubs = "Discover courts, gyms and beach spots near you, see where people play, and book a place for your next game.",
    ),
)

private fun sportEnum(slug: String): String = slug.uppercase()

private fun sportIcon(slug: String): String? = when (slug) {
    "football" -> AppIcons.SportFootball
    "basketball" -> AppIcons.SportBasketball
    "volleyball" -> AppIcons.SportVolleyball
    "tennis" -> AppIcons.SportTennis
    "padel" -> AppIcons.SportPadel
    else -> null
}

@Composable
fun SportLandingContent(sport: SportSeo) {
    val ctx = rememberPageContext()
    PageLayout(
        seoTitle = sport.seoTitle,
        description = sport.description,
        mobileReady = true,
    ) {
        // Hero: sport icon (mask-image tinted) + H1 + intro
        val glyph = sportIcon(sport.slug)
        if (glyph != null) {
            com.varabyte.kobweb.compose.foundation.layout.Box(
                modifier = Modifier.styleModifier {
                    property("width", "56px")
                    property("height", "56px")
                    property("background-color", "#0061FF")
                    property("-webkit-mask-image", "url(\"$glyph\")")
                    property("mask-image", "url(\"$glyph\")")
                    property("-webkit-mask-size", "contain")
                    property("mask-size", "contain")
                    property("-webkit-mask-repeat", "no-repeat")
                    property("mask-repeat", "no-repeat")
                    property("-webkit-mask-position", "center")
                    property("mask-position", "center")
                }
            )
        }
        H1(attrs = { style { property("display", "contents") } }) {
            SpanText(
                sport.h1,
                modifier = Modifier.h1().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
        SpanText(
            sport.intro,
            modifier = Modifier.bodyLRegular().color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
        // Feature blocks
        InfoSection("Create or join a game") { InfoParagraph(sport.createJoin) }
        InfoSection("Find players at your level") { InfoParagraph(sport.findPlayers) }
        InfoSection("Discover clubs and courts") { InfoParagraph(sport.clubs) }
        // CTA to Arena map
        NavAnchor(
            "/arena?sport=${sportEnum(sport.slug)}",
            onNavigate = { ctx.router.navigateTo("/arena?sport=${sportEnum(sport.slug)}") }
        ) {
            SpanText(
                "Open ${sport.name.lowercase()} on the map →",
                modifier = Modifier.bodyLRegular().color(AppColors.Primary)
            )
        }
        // Arena club grid (baked, crawlable club names)
        val clubs = GENERATED_SPORT_CLUBS[sport.slug].orEmpty()
        if (clubs.isNotEmpty()) {
            if (isMobile()) {
                com.arena_web.ui.sections.arena.mobile.MobileClubGrid(
                    clubs = clubs,
                    isLoadingMore = false,
                    onLoadMore = {},
                    sportName = sportEnum(sport.slug),
                    onClubClick = { ctx.router.navigateTo("/clubs/${it.id}") },
                )
            } else {
                com.arena_web.pages.ClubGrid(
                    clubs = clubs,
                    isLoadingMore = false,
                    onLoadMore = {},
                    sportName = sportEnum(sport.slug),
                    onClubClick = { ctx.router.navigateTo("/clubs/${it.id}") },
                )
            }
        }
        // Store badges + navigation links
        StoreBadges()
        Row(modifier = Modifier.styleModifier { property("gap", "16px"); property("flex-wrap", "wrap") }) {
            NavAnchor("/sports", onNavigate = { ctx.router.navigateTo("/sports") }) {
                SpanText("All sports", modifier = Modifier.bodyLRegular().color(AppColors.Primary))
            }
            NavAnchor("/cities", onNavigate = { ctx.router.navigateTo("/cities") }) {
                SpanText("Browse by city", modifier = Modifier.bodyLRegular().color(AppColors.Primary))
            }
            NavAnchor("/about", onNavigate = { ctx.router.navigateTo("/about") }) {
                SpanText("What is Arena Pro Play?", modifier = Modifier.bodyLRegular().color(AppColors.Primary))
            }
        }
    }
}

@Composable
fun SportsIndexContent() {
    val ctx = rememberPageContext()
    PageLayout(
        seoTitle = "Sports on Arena Pro Play — Find Players & Games",
        description = Constants.PRODUCT_DEFINITION,
        mobileReady = true,
    ) {
        InfoPageScaffold(title = "Find Players & Games for Your Sport", titleIsH1 = true) {
            InfoParagraph(
                "Arena Pro Play helps you find people to play with, whatever your sport. " +
                    "Pick yours below to find players near you, join local games, and discover " +
                    "clubs and courts."
            )
            ALL_SPORTS.forEach { sport ->
                InfoSection(sport.name) {
                    InfoParagraph(sport.teaser)
                    NavAnchor("/${sport.slug}", onNavigate = { ctx.router.navigateTo("/${sport.slug}") }) {
                        InfoParagraph("Find ${sport.name.lowercase()} players near you →")
                    }
                }
            }
            NavAnchor("/cities", onNavigate = { ctx.router.navigateTo("/cities") }) {
                InfoParagraph("Or browse players and clubs by city →")
            }
        }
    }
}
